import InfoIcon from '@mui/icons-material/Info';
import { Button, Checkbox, Dialog, DialogActions, DialogContent, FormControl, FormControlLabel, Grid, MenuItem, Select, SelectChangeEvent, TextField, Tooltip, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import { Plus, X } from '../../../assets';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { PostReasonRequest } from '../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import useLogError from '../../../hooks/useLogError';

import { Reason } from '../../../app/services/api/generated';

export interface Reasons {
  onClose: () => void;
  onConfirm: () => void;
  reasonId: number;
  name: string | undefined;
  description: string
  actionType: string
  includeInWasteLog: boolean;
  wasteLogSortOrder: number | null;
  wasteLogReasonsCount: number;
  existingReasons: Reason[],
}

interface ActionTypes {
  description: string,
}
const actionTypes: ActionTypes[] = [
  { description: 'Issue' },
  { description: 'Receive' },
];

const EditReasonModal = (props: Reasons) => {
  const { putTransactionReasons } = useNswagClient();
  const { logError } = useLogError();
  const [description, setDescription] = useState<string>();
  const [name, setName] = useState<string>('');
  const [actionType, setActionType] = useState<string>();
  const [reasonId, setReasonId] = useState<number>();
  const [includeInWasteLog, setIncludeInWasteLog] = useState<boolean>(false);
  const [wasteLogSortOrder, setWasteLogSortOrder] = useState<number | null>(null);
  const [showMaxError, setShowMaxError] = useState<boolean>(false);
  const [showDuplicateSortOrderError, setShowDuplicateSortOrderError] = useState<boolean>(false);
  const { t } = useTranslation('manageTransactions');
  const readonlyReason = 'Mark Out';

  useEffect(() => {
    const convertActionType = props.actionType.charAt(0).toUpperCase() + props.actionType.slice(1).toLowerCase();
    setActionType(convertActionType);
    setReasonId(props.reasonId);
    setName(props.name!);
    setDescription(props.description);
    setIncludeInWasteLog(props.includeInWasteLog);
    setWasteLogSortOrder(props.wasteLogSortOrder);
  }, [props.reasonId, props.actionType, props.description, props.name, props.includeInWasteLog, props.wasteLogSortOrder]);

  const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value);
  };

  const handleSelect = (event: SelectChangeEvent<string>) => {
    setActionType(event.target.value);
  };

  const handleIncludeInWasteLogChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked && !props.includeInWasteLog && props.wasteLogReasonsCount >= 4) {
      setShowMaxError(true);
      return;
    }
    setShowMaxError(false);
    setIncludeInWasteLog(checked);
    // Clear sort order when removing from waste log
    if (!checked) {
      setWasteLogSortOrder(null);
    }
  };

  const handleSortOrderChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!includeInWasteLog) return;

    setShowDuplicateSortOrderError(false);
    const value = event.target.value;
    if (value === '') {
      setWasteLogSortOrder(null);
    } else {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue >= 1 && numValue <= 4) {
        setWasteLogSortOrder(numValue);
      }
    }
  };

  const updateReason = () => {
    // Check for duplicate sort order (excluding current reason)
    if (includeInWasteLog && wasteLogSortOrder) {
      const hasDuplicate = props.existingReasons.some(
        r => r.id !== reasonId && r.includeInWasteLog && r.wasteLogSortOrder === wasteLogSortOrder,
      );
      if (hasDuplicate) {
        setShowDuplicateSortOrderError(true);
        return;
      }
    }

    const body: PostReasonRequest = {
      name: name,
      description: description,
      actionType: actionType,
      includeInWasteLog: includeInWasteLog,
      wasteLogSortOrder: includeInWasteLog ? wasteLogSortOrder ?? undefined : undefined,
    };
    const id = reasonId ?? 0;
    putTransactionReasons(id, body).then(() => {
      props.onConfirm();
    })
      .catch((error) => {
        logError(error);
      });
  };
  const handleClose = () => {
    setName(props.name!);
    setActionType(props.description);
    setActionType(props.actionType);
    setWasteLogSortOrder(props.wasteLogSortOrder);
    setShowMaxError(false);
    setShowDuplicateSortOrderError(false);
    props.onClose();
  };

  return (
    <Dialog open={true}
      onClose={handleClose}
      maxWidth='md'
      fullWidth>
      <Grid
        sx={{ m: '2px' }}>
        <Grid
          sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
          <X onClick={props.onClose} />
        </Grid>
        <Grid
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant='textXL'>{t('editTitle')}</Typography>
        </Grid>
      </Grid>
      <DialogContent>
        <Grid sx={{ pt: '30px' }}>
          <TextField
            placeholder={t('createTitle')}
            multiline
            fullWidth
            disabled={true}
            value={props.name}
          />
        </Grid>
        <Grid sx={{ pt: '30px' }}>
          <TextField
            placeholder={t('description')}
            value={description}
            multiline
            rows={5}
            fullWidth
            onChange={handleDescriptionChange}
          />
        </Grid>
        <Grid sx={{ pt: '30px' }}>
          <FormControl
            fullWidth>
            <Select
              value={actionType}
              disabled={props.name === readonlyReason}
              onChange={handleSelect}
              fullWidth>
              {actionTypes.map((option) => (
                <MenuItem key={option.description}
                  value={option.description}>
                  {option.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid sx={{ pt: '30px' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeInWasteLog}
                onChange={handleIncludeInWasteLogChange}
                disabled={!props.includeInWasteLog && props.wasteLogReasonsCount >= 4}
              />
            }
            label={<>{t('includeInWasteLog')}<Tooltip title={t('maxWasteReasonsError')}>
              <InfoIcon fontSize='small'
                sx={{ ml: 2, verticalAlign: 'sub' }} />
            </Tooltip></>}
          />
          {showMaxError && (
            <Typography
              color="error"
              variant="textSM"
              sx={{ mt: 1 }}
            >
              {t('maxWasteReasonsError')}
            </Typography>
          )}
        </Grid>
        {includeInWasteLog && (
          <Grid sx={{ pt: '20px' }}>
            <TextField
              label={t('wasteLogSortOrder')}
              type="number"
              fullWidth
              value={wasteLogSortOrder ?? ''}
              onChange={handleSortOrderChange}
              inputProps={{ min: 1, max: 4 }}
              helperText={t('wasteLogSortOrderHelper')}
              error={showDuplicateSortOrderError}
            />
            {showDuplicateSortOrderError && (
              <Typography
                color="error"
                variant="textSM"
                sx={{ mt: 1 }}
              >
                Another reason already uses this sort order. Please choose a different position.
              </Typography>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions >
        <Button
          variant="secondary"
          onClick={handleClose}> {t('cancel')}</Button>
        <Button onClick={updateReason}
        ><Plus />
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditReasonModal;